import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";

const GALLERY = [
  { src: "/images/media/rks-1.jpg", alt: "White-gold blue-stone necklace and earring suite on display bust — Ramesh Kapoor & Sons Jewellers", caption: "Blue-Stone Suite" },
  { src: "/images/media/rks-5.jpg", alt: "Gold link ring on dark marble — Ramesh Kapoor & Sons Jewellers", caption: "Gold Link Ring" },
  { src: "/images/media/rks-4.jpg", alt: "Pearl and diamond-set bangle on stone — Ramesh Kapoor & Sons Jewellers", caption: "Pearl & Diamond-Set Bangle" },
  { src: "/images/media/rks-3.jpg", alt: "Blue-stone and diamond necklace with matching earrings on navy bust — Ramesh Kapoor & Sons Jewellers", caption: "Necklace & Earring Suite" },
  { src: "/images/media/rks-2.jpg", alt: "Diamond-set link bracelet — Ramesh Kapoor & Sons Jewellers", caption: "Diamond-Set Link Bracelet" },
  { src: "/images/media/rks-10.jpg", alt: "Rose-gold bangle with pink stones and diamond accents on dark marble — Ramesh Kapoor & Sons Jewellers", caption: "Pink-Stone Bangle" },
  { src: "/images/media/rks-9.jpg", alt: "Blue-stone pendant with diamond detailing and matching earrings — Ramesh Kapoor & Sons Jewellers", caption: "Pendant & Earring Suite" },
  { src: "/images/media/rks-8.jpg", alt: "Gold link chain on navy display bust — Ramesh Kapoor & Sons Jewellers", caption: "Gold Link Chain" },
  { src: "/images/media/rks-7.jpg", alt: "Two-tone hexagonal link bangle on dark marble — Ramesh Kapoor & Sons Jewellers", caption: "Two-Tone Hexagon Bangle" },
  { src: "/images/media/rks-6.jpg", alt: "Rose-gold screw-motif bangle on dark marble — Ramesh Kapoor & Sons Jewellers", caption: "Screw-Motif Bangle" },
];

function Lightbox({ index, onClose, onPrev, onNext }) {
  const item = GALLERY[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      data-testid="media-lightbox"
      className="fixed inset-0 z-[80] bg-[#05080E]/[0.96] backdrop-blur-xl flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 sm:px-10 h-20 shrink-0">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FBF9F5]/50" data-testid="lightbox-counter">
          {index + 1} / {GALLERY.length}
        </p>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          aria-label="Close viewer"
          className="text-[#FBF9F5]/70 hover:text-[#C5A059] transition-colors p-2"
        >
          <X size={24} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 sm:px-24 pb-6 min-h-0" onClick={(e) => e.stopPropagation()}>
        <motion.img
          key={item.src}
          src={item.src}
          alt={item.alt}
          data-testid="lightbox-image"
          className="max-h-full max-w-full object-contain shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        />
      </div>
      <div className="flex items-center justify-between px-6 sm:px-10 pb-8 shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="lightbox-prev"
          onClick={onPrev}
          aria-label="Previous photograph"
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#FBF9F5]/60 hover:text-[#C5A059] transition-colors p-2"
        >
          <ChevronLeft size={16} /> Prev
        </button>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059] font-medium" data-testid="lightbox-caption">
          {item.caption}
        </p>
        <button
          data-testid="lightbox-next"
          onClick={onNext}
          aria-label="Next photograph"
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#FBF9F5]/60 hover:text-[#C5A059] transition-colors p-2"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}

export default function Media() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const prev = useCallback(() => setLightbox((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);
  const next = useCallback(() => setLightbox((i) => (i + 1) % GALLERY.length), []);
  const close = useCallback(() => setLightbox(null), []);

  return (
    <div data-testid="media-page" className="pt-28 sm:pt-36">
      <SEO title="Media & Updates"
        description="News and announcements from RK Group, Ramesh Kapoor and Sons Jewellers Pvt. Ltd., and RK Builders." />
      <header className="max-w-[1000px] mx-auto px-5 sm:px-8 pb-14">
        <Eyebrow>RK Group</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4">
          Media &amp; <span className="italic text-[#C5A059]">Updates</span>
        </h1>
        <p className="text-base text-[#94A3B8] leading-loose mt-6 max-w-2xl">
          Announcements from RK Group, Ramesh Kapoor and Sons Jewellers Pvt. Ltd., and RK Builders.
        </p>
      </header>
      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <Eyebrow>From the Jewellery House</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] mt-4 mb-12">
            The Collection, <span className="italic text-[#C5A059]">in Focus</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5" data-testid="media-gallery">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.06}
              className={`group relative overflow-hidden border border-white/10 ${i === 0 ? "col-span-2 row-span-2 aspect-[4/5] sm:aspect-auto" : "aspect-[4/5]"}`}>
              <img src={g.src} alt={g.alt} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#060A12]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="pointer-events-none absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-[#FBF9F5] font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {g.caption}
              </p>
              <button
                data-testid={`media-item-${i}`}
                onClick={() => setLightbox(i)}
                aria-label={`View ${g.caption} full-screen`}
                className="absolute inset-0 z-10 cursor-pointer"
              />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 pb-28">
        {loading ? (
          <p className="text-sm text-[#94A3B8]" data-testid="news-loading">Loading…</p>
        ) : news.length === 0 ? (
          <div className="border border-white/10 p-14 text-center" data-testid="news-empty">
            <p className="font-serif text-2xl text-[#FBF9F5]">Announcements will be published here.</p>
          </div>
        ) : (
          <div className="space-y-px">
            {news.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.05} className="border border-white/10 bg-[#0B1320] p-8 sm:p-10">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] border border-[#C5A059]/30 px-3 py-1">{n.tag}</span>
                  <span className="text-[11px] text-[#94A3B8]">{fmtDateTime(n.date)}</span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] mt-4">{n.title}</h2>
                <p className="text-sm text-[#94A3B8] leading-loose mt-4 max-w-3xl">{n.body}</p>
              </Reveal>
            ))}
          </div>
        )}
        <div className="mt-12 text-center">
          <Link to="/contact?type=media" data-testid="media-cta-btn"
            className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-10 py-4 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
            Media Enquiries
          </Link>
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox index={lightbox} onClose={close} onPrev={prev} onNext={next} />
        )}
      </AnimatePresence>
    </div>
  );
}
